import axios, { AxiosInstance } from 'axios';
import { HttpAdapter } from './http.adapter';
import { handleHttpError } from '../../../Utils/Http/httpErrors.util';

interface Options {
   baseUrl: string;
   params: Record<string, any>;
}

export type ApiError = {
   status: number;
   message: string;
   data?: any;
   errors?: Record<string, string[]>;
   headers?: Record<string, any>;
};

export class AxiosAdapter implements HttpAdapter {
   private axiosInstance: AxiosInstance;

   constructor(options: Options) {
      this.axiosInstance = axios.create({
         baseURL: options.baseUrl,
         params: options.params,
         headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
         },
      });

      this.axiosInstance.interceptors.request.use(
         async (config) => {
            const accessToken = localStorage.getItem('serviceToken');
            if (accessToken) {
               config.headers['Authorization'] = `Bearer ${accessToken}`;
            }
            return config;
         },
         (error) => {
            return Promise.reject(error);
         }
      );

      this.axiosInstance.interceptors.response.use(
         (response) => response,
         (error): Promise<ApiError> => {
            if (error.response) {
               handleHttpError(error.response.status);

               return Promise.reject({
                  status: error.response.status,
                  message:
                     error.response.data?.message || 'Error en la solicitud',
                  data: error.response.data,
                  errors: error.response.data?.errors,
                  headers: error.response.headers,
               });
            } else {
               const networkError: ApiError = {
                  status: 0,
                  message: 'Error de conexión con el servidor',
               };

               console.error('Error de red o conexión:', error);
               return Promise.reject(networkError);
            }
         }
      );
   }

   setSession(serviceToken: string): void {
      if (serviceToken) {
         localStorage.setItem('serviceToken', serviceToken);
         axios.defaults.headers.common['Authorization'] =
            `Bearer ${serviceToken}`;
      } else {
         localStorage.removeItem('serviceToken');
         delete axios.defaults.headers.common['Authorization'];
      }
   }

   async get<T>(url: string, options?: Record<string, any>): Promise<T> {
      try {
         const { data } = await this.axiosInstance.get(url, options);
         return data;
      } catch (error) {
         throw error;
      }
   }

   async post<T>(
      url: string,
      body: Record<string, any>,
      options?: Record<string, any>
   ): Promise<T> {
      try {
         const { data } = await this.axiosInstance.post(url, body, options);
         return data;
      } catch (error) {
         throw error;
      }
   }

   async put<T>(
      url: string,
      body: Record<string, any>,
      options?: Record<string, any>
   ): Promise<T> {
      try {
         const { data } = await this.axiosInstance.put(url, body, options);
         return data;
      } catch (error) {
         throw error;
      }
   }

   async patch<T>(
      url: string,
      body?: Record<string, any>,
      options?: Record<string, any>
   ): Promise<T> {
      try {
         const { data } = await this.axiosInstance.patch(url, body, options);
         return data;
      } catch (error) {
         throw error;
      }
   }

   async delete<T>(url: string, options?: Record<string, any>): Promise<T> {
      try {
         const { data } = await this.axiosInstance.delete(url, options);
         return data;
      } catch (error) {
         throw error;
      }
   }
}
